/**
 * 集中式错误码。前后端共用，避免用字符串散落各处。
 * code 用于程序判断，message 是给用户看的中文文案。
 */
export const ERROR_CODES = {
  // 通用
  VALIDATION_FAILED: 'VALIDATION_FAILED',
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  NOT_FOUND: 'NOT_FOUND',
  CONFLICT: 'CONFLICT',
  INTERNAL: 'INTERNAL',

  // 认证
  USERNAME_TAKEN: 'USERNAME_TAKEN',
  EMAIL_TAKEN: 'EMAIL_TAKEN',
  INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
  EMAIL_NOT_VERIFIED: 'EMAIL_NOT_VERIFIED',
  CAPTCHA_REQUIRED: 'CAPTCHA_REQUIRED',
  CAPTCHA_INVALID: 'CAPTCHA_INVALID',
  CAPTCHA_EXPIRED: 'CAPTCHA_EXPIRED',
  TOTP_REQUIRED: 'TOTP_REQUIRED',
  TOTP_INVALID: 'TOTP_INVALID',
  ACCOUNT_LOCKED: 'ACCOUNT_LOCKED',
  TOKEN_INVALID: 'TOKEN_INVALID',
  TOKEN_EXPIRED: 'TOKEN_EXPIRED',
  TOKEN_USED: 'TOKEN_USED',
  SESSION_REVOKED: 'SESSION_REVOKED',
  /**
   * 批量"移除其它全部设备"时**认不出当前会话**（Refresh Cookie 缺失或刚被轮换）。
   *
   * 单独给一个错误码，是因为这条路径的正确反应是"什么都不做"而不是"那就都删了吧"：
   * 宁可让用户刷新页面重试，也不能把正在用的设备一起吊销、把用户踢下线。
   */
  CURRENT_SESSION_UNKNOWN: 'CURRENT_SESSION_UNKNOWN',
  WEAK_PASSWORD: 'WEAK_PASSWORD',
  PASSWORD_REUSED: 'PASSWORD_REUSED',
  RATE_LIMITED: 'RATE_LIMITED',

  // 学生与共享
  STUDENT_NOT_FOUND: 'STUDENT_NOT_FOUND',
  STUDENT_USERNAME_TAKEN: 'STUDENT_USERNAME_TAKEN',
  SHARE_ALREADY_EXISTS: 'SHARE_ALREADY_EXISTS',
  SHARE_NOT_PENDING: 'SHARE_NOT_PENDING',
  SHARE_SELF: 'SHARE_SELF',
  SHARE_TARGET_NOT_FOUND: 'SHARE_TARGET_NOT_FOUND',
  CANNOT_MODIFY_OWNER: 'CANNOT_MODIFY_OWNER',

  // 目标 / 任务
  NODE_NOT_FOUND: 'NODE_NOT_FOUND',
  NODE_DEPTH_EXCEEDED: 'NODE_DEPTH_EXCEEDED',
  /**
   * @deprecated `kind` 已不再约束挂载（一棵树，见 packages/db/sql/triggers.sql 的 guard_node_tree）。
   * 保留取值只为兼容历史前端与日志，**新代码不要再抛它**。
   */
  NODE_KIND_MISMATCH: 'NODE_KIND_MISMATCH',
  NODE_DATE_OUT_OF_PARENT_RANGE: 'NODE_DATE_OUT_OF_PARENT_RANGE',
  /** 上级有截止日期时，子级不能设为"长期"（触发器 guard_node_tree 强制）。 */
  NODE_LONG_TERM_UNDER_DATED_PARENT: 'NODE_LONG_TERM_UNDER_DATED_PARENT',
  /** 改上级日期会让**某个子孙**越界（触发器回头校验，报错信息里带越界子目标名）。 */
  NODE_DESCENDANT_OUT_OF_RANGE: 'NODE_DESCENDANT_OUT_OF_RANGE',
  NODE_NOT_LEAF: 'NODE_NOT_LEAF',
  NODE_READONLY_FOR_STUDENT: 'NODE_READONLY_FOR_STUDENT',
  NODE_HAS_CHECKINS: 'NODE_HAS_CHECKINS',
  /** 当前状态不允许该动作（例如对"完成"的目标再按"暂停"）。details 带 action/current/allowed。 */
  NODE_INVALID_STATE: 'NODE_INVALID_STATE',
  /** 标记完成被拒：还有子目标未完成。details.incomplete 是[{id,name,status,statusLabel}]。 */
  NODE_CHILDREN_INCOMPLETE: 'NODE_CHILDREN_INCOMPLETE',
  /** 标记完成被拒：有子目标已**停止**（"全部完成"逻辑上不成立，应改为「停止」）。 */
  NODE_CHILD_STOPPED: 'NODE_CHILD_STOPPED',

  // 打卡与计时
  CHECKIN_NOT_FOUND: 'CHECKIN_NOT_FOUND',
  CHECKIN_INVALID_STATE: 'CHECKIN_INVALID_STATE',
  CHECKIN_ALREADY_STARTED: 'CHECKIN_ALREADY_STARTED',
  CHECKIN_NOT_RUNNING: 'CHECKIN_NOT_RUNNING',
  CHECKIN_NOT_TODAY: 'CHECKIN_NOT_TODAY',
  CHECKIN_GRACE_EXPIRED: 'CHECKIN_GRACE_EXPIRED',
  /**
   * 「撤销放弃」超出孩子自己能恢复的范围 —— 孩子只能把**今天**误点的放弃恢复回来。
   *
   * 为什么给孩子限当天（见 `permissions.canRestoreSkippedCheckin`）：
   * 隔天的 `skipped` 已经进了结算（连续天数、统计、周报），
   * 让孩子翻回去改历史，等于给他一支随时能重写过去的笔。
   * 家长不受这条限制（纠错权在家长），所以这里不是"没有权限"，而是"过期了" ——
   * 文案必须把下一句说出来：**让家长来撤销**。
   */
  CHECKIN_RESTORE_EXPIRED: 'CHECKIN_RESTORE_EXPIRED',
  ESTIMATED_DURATION_REQUIRED: 'ESTIMATED_DURATION_REQUIRED',
  /**
   * 「这是孩子自己的事」：这一步**只能由孩子本人**操作，家长（哪怕 owner/editor）也不行。
   *
   * 2026-10-16 用户拍板的规则：家长**不能**替孩子在孩子端打卡/完成/开始计时/补卡，
   * 也不能替孩子发起兑换 —— 他要看，让孩子登录给他看。
   * 理由（用户原话）："如果家长能随意改动孩子的数据，一旦被孩子知道，
   * 他就会对这个系统产生怀疑，产生不信任感。"
   *
   * 单独给一个错误码而不是复用 `FORBIDDEN`：`FORBIDDEN` 是"你没权限碰这条数据"，
   * 而这里是"这条数据本来就该由孩子自己产生" —— 前者要引导家长申请共享权限，
   * 后者要引导家长让孩子自己来做。文案必须说得出这个区别。
   *
   * 注意边界：**家长在自己界面上的合法能力不受影响**
   * （审批作业通过/打回 `canApproveCheckin`、撤销打卡 `canUndoCheckin`、
   *  放弃作业项 `canSkipHomeworkCheckin`、手动调分 `canAdjustPoints`、
   *  维护奖品目录 `canManageRewards`），它们各有各的判定函数。
   */
  STUDENT_ONLY_ACTION: 'STUDENT_ONLY_ACTION',
  HABIT_NOT_FOUND: 'HABIT_NOT_FOUND',
  HABIT_INVALID_RECURRENCE: 'HABIT_INVALID_RECURRENCE',
  /** 已有打卡记录的打卡任务不能物理删除（会连带删掉孩子的历史事实数据）。 */
  HABIT_HAS_CHECKINS: 'HABIT_HAS_CHECKINS',

  // 作业（见 apps/api/src/modules/homework/）
  /**
   * 「复制上次的结构」找不到可复制的来源：
   * 指定（或自动挑选）的那一天没有作业单，或那份作业单一个科目都没有。
   */
  HOMEWORK_NO_SOURCE: 'HOMEWORK_NO_SOURCE',
  /** 整单保存的入参在业务上不成立（重复科目名、时长跨过午夜等，`details.reason` 说明）。 */
  HOMEWORK_INVALID_INPUT: 'HOMEWORK_INVALID_INPUT',
  /**
   * 「放弃」作业项被拒：**只有家长能放弃作业**。
   *
   * 与普通打卡区分开：普通打卡的 `skip` 学生本人可用（既有行为，不改），
   * 而作业项的 `skip` 是"这份作业不做了"的终止决定，学生调用一律 403。
   * 单独给一个错误码而不是复用 `FORBIDDEN`，是为了让前端/日志能区分
   * "你没有权限碰这条数据"与"这条数据只有家长能放弃"。
   */
  HOMEWORK_SKIP_PARENT_ONLY: 'HOMEWORK_SKIP_PARENT_ONLY',

  // 语文听写（见 apps/api/src/modules/dictation/）
  /** 听写会话不存在（或不属于这个孩子）。 */
  DICTATION_SESSION_NOT_FOUND: 'DICTATION_SESSION_NOT_FOUND',
  /** 取词条件一条词都没取到 —— 不建空会话（理由见 dictation.service 的 createSession）。 */
  DICTATION_NO_WORDS: 'DICTATION_NO_WORDS',
  /** 录音体积超限（`details.maxBytes` / `details.bytes` 说明差多少）。 */
  DICTATION_AUDIO_TOO_LARGE: 'DICTATION_AUDIO_TOO_LARGE',
  /** 录音数据不是合法的 base64（浏览器给的 blob 坏了 / 传了 data URL 前缀）。 */
  DICTATION_AUDIO_INVALID: 'DICTATION_AUDIO_INVALID',
  /** 这个词没有录音（`GET .../audios/:wordId` 用它，前端据此回落浏览器语音）。 */
  DICTATION_AUDIO_NOT_FOUND: 'DICTATION_AUDIO_NOT_FOUND',
  /**
   * 这次听写**还不能批改**（会话不是 `completed`）。
   *
   * 单独一个错误码而不是复用 `VALIDATION_FAILED`：界面上"还没听完呢"与
   * "提交的内容有误"是两句完全不同的话 —— 前者要引导家长先让孩子听完。
   */
  DICTATION_NOT_GRADEABLE: 'DICTATION_NOT_GRADEABLE',
  /** 错词本里没有这个词（手动"已掌握"时给错词 id）。 */
  DICTATION_MISTAKE_NOT_FOUND: 'DICTATION_MISTAKE_NOT_FOUND',
  /**
   * 批量任务不存在（或不属于这个孩子）—— 迁移 0020。
   *
   * 与 `DICTATION_SESSION_NOT_FOUND` 分开：孩子点「开始听写」时后端要告诉他
   * "这个任务已经不在了"，而不是"这次听写找不到了"，两句话在界面上指向
   * 完全不同的下一步（回任务列表 vs 重建会话）。
   */
  DICTATION_ASSIGNMENT_NOT_FOUND: 'DICTATION_ASSIGNMENT_NOT_FOUND',
  /**
   * 批量任务的词**已经全部听过**（"取下一批"没有词可取了）。
   *
   * ⚠️ 不复用 `DICTATION_NO_WORDS`：那个的意思是"你的筛选条件太窄了"，
   *   而这里恰恰相反 —— 条件没问题，是**这个任务已经做完了**。
   *   界面上前者要引导去放宽条件，后者要显示"听写完成 🎉"。
   */
  DICTATION_ASSIGNMENT_DONE: 'DICTATION_ASSIGNMENT_DONE',
  /**
   * 任务当前不是「进行中」（家长暂停 / 已结束）。
   *
   * 孩子端点「开始听写」时给 409 + `details.status`，界面据此显示
   * "家长先暂停了这个任务"，**而不是**一个通用的失败提示。
   */
  DICTATION_ASSIGNMENT_INACTIVE: 'DICTATION_ASSIGNMENT_INACTIVE',

  // 积分与兑换
  INSUFFICIENT_POINTS: 'INSUFFICIENT_POINTS',
  REWARD_NOT_FOUND: 'REWARD_NOT_FOUND',
  REWARD_OUT_OF_STOCK: 'REWARD_OUT_OF_STOCK',
  REWARD_INACTIVE: 'REWARD_INACTIVE',
  REDEMPTION_NOT_FOUND: 'REDEMPTION_NOT_FOUND',
  REDEMPTION_INVALID_STATE: 'REDEMPTION_INVALID_STATE',
  DUPLICATE_POINTS_ENTRY: 'DUPLICATE_POINTS_ENTRY',
  LEDGER_IMMUTABLE: 'LEDGER_IMMUTABLE',

  // 邮件
  MAIL_DISABLED: 'MAIL_DISABLED',
  MAIL_SEND_FAILED: 'MAIL_SEND_FAILED',
} as const;

export type ErrorCode = (typeof ERROR_CODES)[keyof typeof ERROR_CODES];

const MESSAGES: Record<ErrorCode, string> = {
  VALIDATION_FAILED: '提交的内容有误，请检查后重试',
  UNAUTHORIZED: '请先登录',
  FORBIDDEN: '你没有权限执行此操作',
  NOT_FOUND: '未找到对应数据',
  CONFLICT: '数据冲突，请刷新后重试',
  INTERNAL: '服务器开小差了，请稍后再试',

  USERNAME_TAKEN: '该用户名已被使用',
  EMAIL_TAKEN: '该邮箱已被注册',
  INVALID_CREDENTIALS: '用户名或密码不正确',
  EMAIL_NOT_VERIFIED: '邮箱尚未验证，请先完成邮箱验证',
  CAPTCHA_REQUIRED: '请输入图形验证码',
  CAPTCHA_INVALID: '图形验证码不正确',
  CAPTCHA_EXPIRED: '图形验证码已过期，请重新获取',
  TOTP_REQUIRED: '请输入动态口令',
  TOTP_INVALID: '动态口令不正确',
  ACCOUNT_LOCKED: '账号因多次登录失败已被临时锁定，请稍后再试',
  TOKEN_INVALID: '链接或凭证无效',
  TOKEN_EXPIRED: '链接已过期，请重新获取',
  TOKEN_USED: '该链接已被使用过',
  SESSION_REVOKED: '登录状态已失效，请重新登录',
  CURRENT_SESSION_UNKNOWN:
    '没能确认你当前用的是哪台设备（登录凭证刚被刷新过？），为避免把你自己踢下线，这次批量移除没有执行。请刷新页面后重试。',
  WEAK_PASSWORD: '密码强度不足：至少 12 位，且不能是常见弱口令或包含用户名/邮箱',
  PASSWORD_REUSED: '新密码不能与当前密码相同',
  RATE_LIMITED: '操作过于频繁，请稍后再试',

  STUDENT_NOT_FOUND: '未找到该学生',
  STUDENT_USERNAME_TAKEN: '该学生用户名已被占用',
  SHARE_ALREADY_EXISTS: '已经存在与这位家长的共享关系',
  SHARE_NOT_PENDING: '该共享邀请已被处理',
  SHARE_SELF: '不能把学生共享给自己',
  SHARE_TARGET_NOT_FOUND: '未找到该家长账号，请确认用户名或邮箱',
  CANNOT_MODIFY_OWNER: '不能修改创建者的权限',

  NODE_NOT_FOUND: '未找到该目标或任务',
  NODE_DEPTH_EXCEEDED: '目标/任务最多三级，不能在第三级下继续添加',
  NODE_KIND_MISMATCH: '目标与任务不能互相挂载',
  NODE_DATE_OUT_OF_PARENT_RANGE: '子项的日期不能超出上级的时间范围',
  NODE_LONG_TERM_UNDER_DATED_PARENT: '上级有截止日期时，子目标不能设为「长期」——请给它一个不晚于上级的截止日期',
  NODE_DESCENDANT_OUT_OF_RANGE: '这样修改会让下面的子目标超出时间范围，请先调整它们',
  NODE_NOT_LEAF: '只有最后一级才能创建打卡任务',
  NODE_READONLY_FOR_STUDENT: '这是家长创建的内容，你只能查看和打卡，不能修改',
  NODE_HAS_CHECKINS: '该内容下已有打卡记录，不能直接删除；请改为归档',
  NODE_INVALID_STATE: '当前状态不支持该操作',
  NODE_CHILDREN_INCOMPLETE: '还有子目标未完成，不能标记为完成',
  NODE_CHILD_STOPPED: '有子目标已停止，请改为「停止」',

  CHECKIN_NOT_FOUND: '未找到该打卡任务',
  CHECKIN_INVALID_STATE: '当前状态不支持该操作',
  CHECKIN_ALREADY_STARTED: '该打卡已经开始计时',
  CHECKIN_NOT_RUNNING: '该打卡当前没有在计时',
  CHECKIN_NOT_TODAY: '只能打卡当天的任务',
  CHECKIN_GRACE_EXPIRED: '已超过补卡期限',
  CHECKIN_RESTORE_EXPIRED: '只能撤销「今天」误点的放弃；更早的记录请让家长来撤销',
  ESTIMATED_DURATION_REQUIRED: '请先填写预计完成时长',
  STUDENT_ONLY_ACTION: '这一步需要孩子自己操作：家长可以查看孩子的记录，但不能替他打卡、完成或兑换',
  HABIT_NOT_FOUND: '未找到该打卡任务',
  HABIT_INVALID_RECURRENCE: '打卡频率设置不完整',
  HABIT_HAS_CHECKINS: '孩子已经在这条任务上打过卡，不能删除；请改用「停用」——停用会保留全部历史统计',

  HOMEWORK_NO_SOURCE: '没有找到可以复制的历史作业结构，请先在那一天录入一次作业',
  HOMEWORK_INVALID_INPUT: '作业内容有误，请检查科目与作业项',
  HOMEWORK_SKIP_PARENT_ONLY: '作业的「放弃」只能由家长来点：这一项会一直保留到完成或家长放弃为止',

  DICTATION_SESSION_NOT_FOUND: '未找到这次听写（可能已被删除）',
  DICTATION_NO_WORDS: '这些条件下一个词都没取到，不能开始听写；请放宽年级或课本条件',
  DICTATION_AUDIO_TOO_LARGE: '这段录音太大了，请录短一点（建议每个词控制在 20 秒以内）',
  DICTATION_AUDIO_INVALID: '这段录音的数据不完整，请重新录一遍',
  DICTATION_AUDIO_NOT_FOUND: '这个词还没有录音，将用浏览器语音朗读',
  DICTATION_NOT_GRADEABLE:
    '这次听写孩子还没做完，先让孩子把听写做完（会话变成「已完成」）再批改',
  DICTATION_MISTAKE_NOT_FOUND: '错词本里没有这个词（可能已经被删掉了）',
  DICTATION_ASSIGNMENT_NOT_FOUND: '未找到这个听写任务（可能已被家长删除）',
  DICTATION_ASSIGNMENT_DONE: '这个听写任务的词已经全部听完了 🎉 可以等家长布置新的任务',
  DICTATION_ASSIGNMENT_INACTIVE: '家长暂时停下了这个听写任务，现在还不能开始',

  INSUFFICIENT_POINTS: '积分不足，还差一些才能兑换',
  REWARD_NOT_FOUND: '未找到该奖品',
  REWARD_OUT_OF_STOCK: '该奖品已兑完',
  REWARD_INACTIVE: '该奖品已下架',
  REDEMPTION_NOT_FOUND: '未找到该兑换记录',
  REDEMPTION_INVALID_STATE: '该兑换当前状态不支持此操作',
  DUPLICATE_POINTS_ENTRY: '这笔积分已经入账过了',
  LEDGER_IMMUTABLE: '积分流水不可修改，请使用冲正操作',

  MAIL_DISABLED: '邮件功能未启用',
  MAIL_SEND_FAILED: '邮件发送失败，请稍后重试',
};

export function errorMessage(code: ErrorCode): string {
  return MESSAGES[code];
}

/** 业务异常。抛出后由 Fastify errorHandler 统一转换为响应体。 */
export class AppError extends Error {
  readonly code: ErrorCode;
  readonly status: number;
  readonly details?: unknown;

  constructor(code: ErrorCode, status = 400, details?: unknown) {
    super(errorMessage(code));
    this.name = 'AppError';
    this.code = code;
    this.status = status;
    this.details = details;
  }

  static unauthorized(code: ErrorCode = ERROR_CODES.UNAUTHORIZED) {
    return new AppError(code, 401);
  }
  static forbidden(code: ErrorCode = ERROR_CODES.FORBIDDEN) {
    return new AppError(code, 403);
  }
  static notFound(code: ErrorCode = ERROR_CODES.NOT_FOUND) {
    return new AppError(code, 404);
  }
  static conflict(code: ErrorCode = ERROR_CODES.CONFLICT) {
    return new AppError(code, 409);
  }
  static rateLimited(code: ErrorCode = ERROR_CODES.RATE_LIMITED) {
    return new AppError(code, 429);
  }
}

/** 统一响应体（见方案 4.6）。 */
export type ApiOk<T> = { ok: true; data: T };
export type ApiErr = {
  ok: false;
  error: { code: ErrorCode; message: string; details?: unknown };
};
export type ApiResponse<T> = ApiOk<T> | ApiErr;
